import palettes from '@mvf/external-components/Themes/palettes';
import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import FetchStatus from '../../Data/FetchStatus';
import MatchConfidenceLevel from '../../Data/MatchConfidenceLevel';
import NewsletterSubscriptionStatus from '../../Data/NewsletterSubscriptionStatus';
import PreloadStatus from '../../Data/PreloadStatus';
import SagaStatus from '../../Data/SagaStatus';
import { isValidCampaignId } from '../../Helpers/campaignId';
import { getPagesBlockedDuringCustomerMatchesRequest } from '../../Helpers/supplierMatches';
import { START_POSITION } from '../../Helpers/clientApiJourneyPosition.helpers';
import { isSupplierMatch } from '../../Helpers/supplierMatchPredicates';
import rootReducer from './reducers/rootReducer';
import rootSaga from './sagas/rootSaga';
import piiTraceMiddleware from './middleware/piiTraceMiddleware';
import SubmissionStatus from '../../Data/SubmissionStatus';
import { getPotentialSubmissionPages } from '../../Helpers/paging';
import { isFormInputComponent } from '../../Helpers/components';
import { getPagesTriggeringPartialSubmission } from '../../Helpers/partialSubmission';
import { buildProgressiveDisplayConfig } from '../../Helpers/progressiveDisplay';
import { getSubmitButtonText } from '../../Components/NavigationBar/navigationBar.helpers';
import {
  buildDynamicSelectAndConnectFeature,
  buildStrategyCollections,
  isDynamicNewsletterPresent,
  isLegalStatementOnFirstPage,
  isOptOut,
} from './store.helpers';

// FIXME: Significant render performance improvement through redux slice split-up
// JIRA ticket: https://mvfglobal.atlassian.net/browse/WF-3904
// TODO: Review the build functions and reducers and ensure that no dynamically
// changing store data is kept inside a multi-data slice. Essentially, when a
// slice contains multiple pieces of data that may change, any one of those
// changes will cause the component that selects a value from that slice to
// re-render. So to ensure that components only re-render when the data values
// they actually depend on change (and not any co-located slice data, we need to
// separate out all dynamically changing data in the store into their own
// reducer and slice)

const buildPaging = (
  formSettings,
  components,
  submissionPageNumber,
  countryCode
) => {
  const pages = formSettings.forms.paging ? formSettings.forms.paging : [];
  const pagesWithLoaderComponent = Object.values(components)
    .filter((component) => component.component.type === 'loader')
    .map((component) => component.component.page);
  const pageNumbers = Object.keys(pages).map((pageNumber) =>
    parseInt(pageNumber, 10)
  );
  const submissionPage = formSettings.forms.metadata?.submission_page;

  const preSubmissionPagesWithQuestions = pageNumbers.filter(
    (pageNumber) =>
      (submissionPage === null || pageNumber <= submissionPage) &&
      !pagesWithLoaderComponent.includes(pageNumber)
  );
  return {
    pageNumbers,
    pages,
    pagesWithLoaderComponent,
    preSubmissionPagesWithQuestions,
    potentialSubmissionPages: getPotentialSubmissionPages({
      components,
      submissionPageNumber,
      pages,
      pageNumbers,
    }),
    partialSubmissionPages: getPagesTriggeringPartialSubmission(
      components,
      formSettings?.forms?.metadata?.enabledPartialSubmission,
      countryCode
    ),
  };
};

const buildNavigation = (formSettings) => {
  const { paging } = formSettings.forms;
  const totalPages = paging.length;

  return {
    currentPage: paging?.currentPage ? paging?.currentPage : 0,
    slideDirection: undefined, // This being set indicates we have navigated
    totalPages,
    isFinalPageBeforeSubmission:
      formSettings.forms.metadata.submission_page === 0 || totalPages === 1,
    isFirstVisiblePostSubmissionPage: false,
    isLoading: false,
    progressBarStages: [],
  };
};

const buildNavigationControls = () => {
  return {
    shouldNavigateForward: false,
    shouldNavigateBackward: false,
    pendingMoveForwards: false, // i.e. are we waiting on a saga to complete
    pendingMoveBackwards: false, // i.e. are we waiting on a saga to complete
    pagesWithNavigationBlocks: {}, // Some requests will prevent navigation to certain pages
    blockedDestinationPage: null, // If an attempt is made to navigate to a page with a block we need to track that page
    attemptToNavigate: false,
    pendingExternalSubmit: false, // Queued external submit request waiting for form to reach final page
  };
};

const buildMarketingConsent = (formSettings) => {
  const { metadata, page_components } = formSettings.forms;
  const { isEmailOptOut, isSmsOptOut, isOttOptOut } = isOptOut(page_components);

  return {
    email: {
      isOptOut: isEmailOptOut,
      isMarketingConsentRequired: metadata.isMarketingConsentRequired,
      location: metadata.marketing_consent_location,
      mainText:
        metadata.chameleon_consent
          .email_marketing_consent_on_thank_you_page_display_main_text,
      submittedText:
        metadata.chameleon_consent
          .email_marketing_consent_on_thank_you_page_display_submitted_text,
    },
    sms: {
      isOptOut: isSmsOptOut,
      isMarketingConsentRequired: metadata.is_sms_marketing_consent_required,
      location: metadata.sms_marketing_consent_location,
      mainText:
        metadata.chameleon_consent
          .sms_marketing_consent_on_thank_you_page_display_main_text,
      submittedText:
        metadata.chameleon_consent
          .sms_marketing_consent_on_thank_you_page_display_submitted_text,
    },
    ott: {
      isOptOut: isOttOptOut,
      isMarketingConsentRequired: metadata.is_ott_marketing_consent_required,
      location: metadata.ott_marketing_consent_location,
      mainText:
        metadata.chameleon_consent
          .ott_marketing_consent_on_thank_you_page_display_main_text,
      submittedText:
        metadata.chameleon_consent
          .ott_marketing_consent_on_thank_you_page_display_submitted_text,
    },
  };
};

const buildFormConfig = (formSettings) => {
  const localeCode = formSettings.forms.metadata.locale_code;
  const [languageCode, countryCode] = localeCode.split('_');

  const {
    isCountdownTimerEnabled,
    countdownTimerOverallTimePlaceholder,
    countdownTimerRemainingTimePlaceholder,
    countdownTimerTextBeforeTimePlaceholder,
  } = formSettings.forms.metadata.countdownTimerSettings;

  return {
    supplierId: formSettings.forms.metadata.gdpr_supplier_id, // Usually this will be an empty string. When it is set as a uuid this indicates that for this form, customers can only be distributed to that supplier.
    shouldRenderForm: true, // show form on first load
    formId: formSettings.primary_webform,
    subcategoryId: formSettings.forms.metadata.subcategory_id,
    tradingHubId: formSettings.forms.metadata.tradingHubId,
    tradingHubName: formSettings.forms.metadata.tradingHub, // name of the trading hub for this form
    subcategoryName: formSettings.forms.metadata.subcategoryName,
    categoryId: formSettings.forms.metadata.subcategoryParentData?.categoryId,
    categoryName:
      formSettings.forms.metadata.subcategoryParentData?.categoryName,
    sectorName: formSettings.forms.metadata.subcategoryParentData?.sectorName,
    countdownTimerSettings: {
      isCountdownTimerEnabled,
      overallTimePlaceholder: countdownTimerOverallTimePlaceholder,
      remainingTimePlaceholder: countdownTimerRemainingTimePlaceholder,
      textBeforeTimePlaceholder: countdownTimerTextBeforeTimePlaceholder,
    },
    privacyPolicy: {
      link: formSettings.forms.metadata.chameleonPrivacyPolicyLink,
      text: formSettings.forms.metadata.chameleonPrivacyPolicyLinkText,
    },
    isDomainInternal: undefined, // set after a call to the gateway to validate the domain
    campaignId: isValidCampaignId(
      formSettings.forms.metadata.default_campaign_id
    )
      ? formSettings.forms.metadata.default_campaign_id
      : undefined, // is a fallback, can be overridden by the browser message
    isExternalPartnerForm:
      formSettings.forms.metadata.chameleonFormPartnerDefault, // Forms marked as chameleonFormPartnerDefault are used on partner sites.
    localeCode, // The original locale code for the form
    countryCode, // The original country code for the form
    languageCode, // The original language code for the form
    phoneNumberCountryCode: countryCode, // this begins as the forms country code but can be edited by user actions
    isPolarisForm: Boolean(
      formSettings.forms.chameleon_settings.polaris_enabled
    ),
    brandLogo: formSettings.forms.chameleon_settings.brand_logo,
    thankYouSectionHeadline:
      formSettings.forms.chameleon_settings.thank_you_section_headline,
    thankYouSectionContent:
      formSettings.forms.chameleon_settings.thank_you_section_content,
    thankYouSectionVideoEmbedUrl:
      formSettings.forms.chameleon_settings.video_embed_url,
    thankYouPageDownloadCtaText:
      formSettings.forms.chameleon_settings.thank_you_page_download_cta_text,
    thankYouPageDownloadContentUrl:
      formSettings.forms.chameleon_settings.thank_you_page_download_content,
    supplierMatchHeadingOnThankYouPage:
      formSettings.forms.chameleon_settings
        .supplier_match_heading_on_thank_you_page,
    sourceSite: {
      value: formSettings.forms.metadata.hidden_fields.source_site.value, // i.e. 'experts.co.uk',
      name: formSettings.forms.metadata.hidden_fields.source_site.name, // i.e. "form_components[159274]"
    },
    headlineText: formSettings.forms.metadata.chameleonHeadlineText,
    headlineLogo: formSettings.forms.chameleon_settings.headline_logo,
    pageUrl: undefined, // set by browser message
    referrerUrl: undefined, // set by browser messages
    thank_you_section_content:
      formSettings.forms.chameleon_settings.thank_you_section_content,
    referenceNumberText:
      formSettings.forms.chameleon_settings.reference_number_text,
    whatHappensNextHeadline:
      formSettings.forms.chameleon_settings.what_happens_next_headline,
    whatHappensNextContent:
      formSettings.forms.chameleon_settings.what_happens_next_content,
    additionalInfoHeadline:
      formSettings.forms.chameleon_settings.additional_info_headline,
    additionalInfoContent:
      formSettings.forms.chameleon_settings.additional_info_content,
    showCustomerMatchesOnThankYouPage:
      formSettings.forms.metadata.showCustomerMatchesOnThankYouPage,
    featureFlags: formSettings.featureFlags,
    fixedComponents: {
      progressBar: {
        text: formSettings.forms?.fixed_components?.progress_bar?.data?.text,
      },
    },
    marketingConsent: buildMarketingConsent(formSettings),
    isTrackingReferrerEnabled:
      formSettings.forms.metadata.isTrackingReferrerEnabled,
    submissionPageNumber: formSettings.forms.metadata.submission_page,
    navigationBarButtonTextConfig:
      buildNavigationBarButtonTextConfig(formSettings),
    subcategoryCountryCode: formSettings.forms.metadata.subcategoryCountryCode,
    enableStagedProgressBar:
      formSettings.forms.metadata.enableStagedProgressBar,
    isProgressBarHidden: formSettings.forms.metadata.isProgressBarHidden,
  };
};

const buildTrustedForm = () => ({
  consentCertification: [],
});

const buildNavigationBarButtonTextConfig = (formSettings) => {
  const initialConfig = formSettings?.forms?.navigationBarButtonTextConfig;

  const submitButtonText = getSubmitButtonText(
    formSettings?.forms?.metadata?.submission_page,
    formSettings?.forms?.paging?.length,
    initialConfig
  );

  return {
    ...initialConfig,
    submit: submitButtonText, // Override the submit button text with a custom CTA if it exists
  };
};

const buildCookies = () => ({
  parentPageCookies: {},
});

const buildSessionDataPopulation = () => {
  return {
    advancePageStatus: undefined,
    questionAndAnswers: {}, // A key:pair of exact question labels (or mapped_question_ids from capture) to a value that has been preset by a partner. i.e. this is how a partner sets value into a question on behalf of the customer. https://widgets.mvfglobal.com/ca/internal/embed.html#answer-presets
  };
};

const buildLegalStatement = (components) => {
  const legalStatementOnFirstPage = isLegalStatementOnFirstPage(components);

  return {
    consentedSuppliersHash: undefined,
    statement: undefined,
    consentedLegalStatementHash: undefined,
    suppliersModal: undefined,
    isSuppliersListModalOpen: false,
    isLegalStatementMounted: legalStatementOnFirstPage,
    shouldRenderLegalStatement: legalStatementOnFirstPage, // in use when dynamicHeight is enabled
    handleSideEffectsOfLegalStatement: false, // in use when dynamicHeight is enabled
    isLegalStatementRevealed: legalStatementOnFirstPage, // in use when dynamicHeight is enabled
    hasStatementGraduallyDisappeared: false, // in use when dynamicHeight is enabled
    newsletters: [],
    sideEffectDetailsForLegalStatement: {
      destinationPageNumber: undefined,
      navigationDirection: undefined,
    }, // in use when dynamicHeight is enabled
    supplierLegalStatements: {
      singleSupplierStatement: null,
      singleSupplierConsentedLegalStatementHash: null,
      multipleSupplierStatement: null,
      multipleSupplierConsentedLegalStatementHash: null,
    },
    stateLegalStatements: undefined, // state-specific variants of the standard statement, e.g. NY; undefined until fetched, null when no state has any
    statementOverride: null,
  };
};

const buildSubmission = (formSettings) => ({
  submissionStatus: SubmissionStatus.NOT_STARTED,
  ancestorSid: undefined, // the original id that the lead was submitted with
  error: {
    message: undefined, // submissionErrorMessage
  },
  submissionId: undefined,
  shouldResubmit: undefined, // Do we need to resubmit the lead? If so we will try and resubmit on render of the form
  token: undefined, // Have changed from submissionToken as that was an echo
  webform: {
    domain: undefined,
    formId: undefined, // think type of this is string
    subcategoryId: formSettings.forms.metadata.subcategory_id,
    url: undefined,
    uuid: undefined,
    components: undefined,
    firstSubmissionAttemptTimestamp: null,
    submissionRetryTimestamp: null,
  },
  partialSubmissionStatus: SubmissionStatus.NOT_STARTED,
  bsaSubmissionStatus: SubmissionStatus.NOT_STARTED,
  latestPartialSubmissionQuestions: undefined,
  selectAndConnectBrands: {},
  submissionBlocks: [],
});

const buildSubmissionStatus = () => ({
  leadId: undefined,
  submissionId: undefined,
  createdAt: undefined,
  status: undefined,
  distribution: {
    completedAt: undefined,
    distributions: undefined, // an array of distribution objects, i.e [ {subscriptionId, supplierId,  distributedAt}]
  },
  selectAndConnectSubscriptions: {
    completedAt: undefined,
    subscriptions: undefined,
  },
});

const buildTheme = (formSettings) => {
  const { themeName } = formSettings;
  return {
    themeName,
    palette: palettes[themeName],
    paletteOverrides: {
      answerBackgroundColor: '#ffffff',
      answerSelectedColor: '#1a9bdb',
      answerUnselectedColor: '#949494',
      backButtonColor: '#ededed',
      backButtonHoverColor: '#dfdfdf',
      continueButtonColor: '#0da587',
      continueButtonHoverColor: '#00755a',
      continueButtonTextColor: '#ffffff',
      primaryTextColor: '#000000',
      scrollIndicatorColor: '#3754a0',
      secondaryBackgroundColor: '#f7f7f7',
      secondaryTextColor: '#4f4f4f',
      widgetBackgroundColor: '#fdfdfd',
    },
  };
};

const buildOptionalFormConfig = () => ({
  iFrameId: undefined, // id of the iframe which the application is attached to
  testMode: false, // TODO confirm default is false
  registeredEventHandlers: [],
  // belowFold: true, // TODO: add only when required
  // borderEnabled: true,  // TODO: add only when required
  // eventHandlersActivated: false, // TODO: add only when required
  isConsentStatementAboveNavigation: false,
  headerEnabled: false, // TODO: is this default ok?
  // height: '575', // TODO: add only when required
  // maxWidth: '800', // TODO: add only when required
  fontOverride: {},
  isDynamicHeightEnabled: undefined, // Toggle to turn on dynamic height of the app - TODO may be redundant soon
  widgetLabel: '', // A partner defined label which gets associated with the lead
  widgetResizeDuration: 500, // time it takes for the widget to resize height during dynamic height calculation of distance
});

const buildDynamicHeight = () => ({
  requiredWidgetHeight: undefined, // the height of the parent iFrame, thus the app is set to this height TODO check this after the dynamic height refactor is complete. This is currently an object with the different sections of the app
  isWidgetHeightRecalculationRequired: undefined,
});

const buildLogRocket = () => ({
  logRocketClientId: 'mvf/chameleon-2wapx',
  isSessionRecordingRequired: false,
});

const buildTransition = () => ({
  enterPhase: false, // enterPhase tracks the exact moment when onEnter fires in the page transition manager <Transition/> that wraps the inputArea. It sets this store value to true the moment the old page is fully unmounted and the new page has begun to mount (assuming that mountOnEnter is false). For more info, read http://reactcommunity.org/react-transition-group/transition#Transition-prop-mountOnEnter
  exitPhase: false, // exitPhase tracks the exact moment when onExit fires in the page transition manager <Transition/> that wraps the inputArea. It sets this store value to true the moment page navigation kicks off and the current page is about to begin unmounting (assuming that unmountOnExit is true). For more info, read http://reactcommunity.org/react-transition-group/transition#Transition-prop-unmountOnExit
  isTransitionAnimationEnabledForSubmission: false, // A custom transition animation may be needed (under certain conditions, currently for the newsletter feature only)
  hasSubmissionPageExitAnimationCompleted: false, // The custom transition consists of an exit animation of the submission page and an enter animation of the next page (newsletter signup)
});

const buildBrowserAutoFill = (formSettings) => {
  const components = Object.fromEntries(
    Object.entries(formSettings.forms.page_components).map((component) => {
      return [
        [component[0]],
        {
          page: component[1].component.page,
          autoFillType: component[1].input?.autoCompleteType
            ? component[1].input?.autoCompleteType
            : '',
        },
      ];
    })
  );

  return { components, isVisible: false };
};

const buildExperimentation = () => ({
  componentExperiments: {},
});

/**
 * buildComponents
 *
 * The option id is a number in the response from capture, but we need it to be a string, hence we convert it here.
 */
export const buildComponents = (formSettings) => {
  // loop over components
  return Object.fromEntries(
    Object.entries(formSettings.forms.page_components).map((component) => {
      const newComponent = { ...component };
      // if component has input
      if (component[1]?.input) {
        // if component has input options
        if (component[1].input?.options) {
          newComponent[1].input.options = component[1].input.options.map(
            (option) => {
              return { ...option, id: String(option.id) };
            }
          );
        }
      }

      // handle conversion of conditional option ids
      if (component[1]?.component && component[1]?.component?.conditional) {
        Object.fromEntries(
          Object.entries(component[1]?.component?.conditional).map(
            (condition) => {
              // handle conversion of onOptionIds from number to string
              if (condition[0] === 'onOptionIds') {
                if (condition[1].length > 0) {
                  newComponent[1].component.conditional.onOptionIds =
                    condition[1].map((optionId) => {
                      return String(optionId);
                    });
                }
              }

              // handle conversion of onComponentIdsOfOtherOptions from number to string
              if (condition[0] === 'onComponentIdsOfOtherOptions') {
                if (condition[1].length > 0) {
                  newComponent[1].component.conditional.onComponentIdsOfOtherOptions =
                    condition[1].map((optionId) => {
                      return String(optionId);
                    });
                }
              }

              return condition;
            }
          )
        );
      }

      // return the component with option ids as strings
      return newComponent;
    })
  );
};

const buildDataMappings = (formSettings) => ({
  mappedQuestionIdToComponentId:
    formSettings.forms.maps.mappedQuestionIdToComponentId,
  urlAliasComponentIdMaps: formSettings.urlAliasComponentIdMaps,
  componentIdToSagaStatus: {},
});

const buildLottieAnimationData = (formSettings) => {
  const { page_components } = formSettings.forms;

  const loaderComponents = Object.values(page_components).filter(
    (component) => {
      return component.component.type === 'loader';
    }
  );

  const loaderConfig = Object.fromEntries(
    loaderComponents.map((component) => {
      return [
        component.component.id,
        {
          animationType: component.component.chameleonLoaderAnimationType,
          animationJson: null,
        },
      ];
    })
  );

  return {
    loaderConfig,
    isRendererAvailable: false,
    loaderAnimation: null,
    submissionSuccessAnimation: null,
  };
};

const buildCrmAutoFill = () => ({
  decryptedData: null,
});

const buildOneTrustLocale = () => ({
  state: undefined,
  country: undefined,
});

const buildCpcCard = () => ({
  preloadStatus: PreloadStatus.NOT_STARTED,
  showCpcRecommendations: false,
  wasPreviousPageCpcCard: false,
  isNextPageCpcCard: false,
});

const buildAppointmentBooking = () => ({
  isNextPageAppointmentBooking: false,
  preloadStatus: PreloadStatus.NOT_STARTED,
  showAppointmentBooking: false,
  wasPreviousPageAppointmentBooking: false,
  selectedAppointment: undefined,
  bookedAppointmentId: undefined,
});

export const buildClientApiJourney = () => ({
  location: {
    listStatus: FetchStatus.NOT_STARTED,
    list: undefined,
    selected: undefined,
  },
  solar: {
    loadStatus: FetchStatus.NOT_STARTED,
    verifyStatus: FetchStatus.NOT_STARTED,
  },
  appointment: {},
  subscriptionMatch: { status: FetchStatus.NOT_STARTED, isMatched: undefined },
  stepOrder: [],
  screenSequence: [],
  position: START_POSITION,
  stepStates: {},
  stepResponses: {},
  isInactivityPromptOpen: false,
  inactivityCountdownSeconds: null,
  journeyTimedOut: false,
  isFallingThrough: false,
  isJourneyOutcomeReported: false,
});

const buildPublicKeys = () => ({
  eventPublicKeyString: null,
});

const buildNewsletter = (components) => ({
  subscriptionStatus: NewsletterSubscriptionStatus.NOT_STARTED,
  isDynamicNewsletterPresent: isDynamicNewsletterPresent(components),
  dynamicNewsletterName: null,
});

const buildSupplierMatches = (formSettings, components, paging) => {
  const subcategoryFallbackBrands =
    formSettings.forms.subcategoryFallbackBrands || [];

  return {
    customerMatches: [], // Indicates dynamically obtained brands to show in supplier match components, tailored for the customers input selections.
    dynamicMatches: [], // A derived view of customerMatches used by the Select and Connect feature: non-exclusive brands are dropped from both customerMatches and the subcategory fallback pool, then non-reseller brands come first (with subcategoryMatches appended as fallbacks when the gateway returns fewer brands than subcategoryMaxMatches), then reseller brands come last so they appear at the bottom of the list. Only populated when features.dynamicSelectAndConnect.enableDynamicSelectAndConnect is true; otherwise stays as [].
    subcategoryMatches: subcategoryFallbackBrands, // The highest revenue brands for the subcategory act as a fallback for dynamic matches.
    customerMatchesRequestStatus: SagaStatus.NOT_STARTED,
    matchingSupplierCount: null,
    matchingSupplierCountSource: null,
    matchingSupplierCountLoading: false,
    matchingSupplierCountLoadingSource: null,
    matchingSupplierCountRequestScheduled: false,
    matchingSupplierCountRequestComponentId: null,
    isSupplierMatchingRequired: Object.values(components).some(isSupplierMatch), // TODO this should not exist in the store as it is derived from the components and misleading as supplier matching may still happen without a supplier match component and it is used in only one place so it should be component state.
    showCustomerMatchesOnThankYouPage:
      formSettings.forms.metadata?.showCustomerMatchesOnThankYouPage,
    pagesBlockedDuringCustomerMatchesRequest:
      getPagesBlockedDuringCustomerMatchesRequest({
        components,
        potentialSubmissionPages: paging.potentialSubmissionPages,
        showCustomerMatchesOnThankYouPage:
          formSettings.forms.metadata?.showCustomerMatchesOnThankYouPage,
      }),
    requiresLegalStatementAboveNav: false,
    matchLevel: undefined,
    supplierMatchSelectionEnabled: true,
    matchConfidence: {
      confidenceLevel: MatchConfidenceLevel.NULL,
      lastShownLevel: MatchConfidenceLevel.NULL,
    },
  };
};

const buildQuestionsWithSideEffectsTriggered = (components) =>
  Object.fromEntries(
    Object.values(components)
      .filter((component) => isFormInputComponent(component))
      .map((component) => [component.component.id, false])
  );

const buildEvents = (formSettings) => ({
  questionsWithSideEffectsTriggered: buildQuestionsWithSideEffectsTriggered(
    formSettings.forms.page_components
  ),
});

const buildIdentities = () => ({
  lytics: undefined,
  visitorId: undefined,
});

const buildErrors = () => ({
  applicationError: null,
});

const initialState = (formSettings) => {
  const components = buildComponents(formSettings);
  const formConfig = buildFormConfig(formSettings);
  const paging = buildPaging(
    formSettings,
    components,
    formConfig.submissionPageNumber,
    formConfig.countryCode
  );

  const features = {
    isBounceNavigationAnimationEnabled: false,
    showSmartMatchMeter: false,
    enableResultsPage: false,
    supplierMatchCountUp: false,
    dynamicSelectAndConnect: buildDynamicSelectAndConnectFeature(components),
  };

  return {
    components,
    clientApiJourney: buildClientApiJourney(),
    strategyCollections: buildStrategyCollections(formSettings),
    cookies: buildCookies(),
    features,
    formConfig,
    legalStatement: buildLegalStatement(components),
    navigation: buildNavigation(formSettings),
    navigationControls: buildNavigationControls(),
    optionalFormConfig: buildOptionalFormConfig(),
    dynamicHeight: buildDynamicHeight(),
    logRocket: buildLogRocket(),
    transition: buildTransition(),
    experimentation: buildExperimentation(),
    paging,
    submission: buildSubmission(formSettings),
    submissionStatus: buildSubmissionStatus(),
    theme: buildTheme(formSettings),
    sessionDataPopulation: buildSessionDataPopulation(),
    browserAutoFill: buildBrowserAutoFill(formSettings),
    dataMappings: buildDataMappings(formSettings),
    lottieAnimationData: buildLottieAnimationData(formSettings),
    crmAutoFill: buildCrmAutoFill(),
    oneTrustLocale: buildOneTrustLocale(),
    cpcCard: buildCpcCard(),
    appointmentBooking: buildAppointmentBooking(),
    progressiveDisplay: buildProgressiveDisplayConfig(components, paging),
    publicKeys: buildPublicKeys(),
    newsletter: buildNewsletter(components),
    supplierMatches: buildSupplierMatches(formSettings, components, paging),
    events: buildEvents(formSettings),
    userIdentities: buildIdentities(),
    errors: buildErrors(),
    trustedForm: buildTrustedForm(),
  };
};

/**
 * preloadedState is how we get the build time data into the store
 */
const buildStore = (formSettings) => {
  const isDevToolsRequired = process.env.NODE_ENV === 'development';
  const sagaMiddleware = createSagaMiddleware();
  const preloadedState = initialState(formSettings);

  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => [
      ...getDefaultMiddleware({
        thunk: false, // We use redux-saga i.e. objects, not the default, thunk i.e. callbacks
        serializableCheck: false, // redux toolkit issues
        immutableCheck: false, // TODO remove this if we can - after the full move to redux toolkit
      }),
      piiTraceMiddleware,
      sagaMiddleware,
    ],
    preloadedState,
    devTools: isDevToolsRequired,
  });
  sagaMiddleware.run(rootSaga);

  return store;
};

export default buildStore;
